import { IPromise } from 'angular';
import { SagaIterator } from 'redux-saga';
import { call, put, takeEvery } from 'redux-saga/effects';

import { Messages } from '../../../codegen/Localize';
import { parseDataPoints } from '../../../metrics';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IMetricInfo } from '../../../ts/models/IMetricInfo';
import { MetricDataPoints } from '../../../ts/resources/MetricDataPoints';
import { DeleteContentsAction } from '../../../ts/services/DeleteContentsAction';
import { ModalInstance, ReactModalService } from '../../../ts/services/ReactModalService';
import { ResumeMetricAction } from '../../../ts/services/ResumeMetricAction';
import { ResumeMetric, UpdateMetricsData } from '../../actions/AppActionTypes';
import { requestFailed } from '../../actions/RequestFailedAction';
import { deleteContentAction } from '../../actions/spaces/DeleteContentAction';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { ResumeMetricAction as ResumeMetricReduxAction, UpdateMetricsDataAction, updateMetricsDataSucceeded } from '../../actions/spaces/UpdateMetricsActions';
import { ResumeMetricErrorDialogProps, resumeMetricErrorModalOptions } from '../../content/ResumeMetricErrorDialog';
import { ServiceLocator } from '../../ServiceLocator';

export async function getMetricsData(metrics: IMetricInfo[]): Promise<IMetricInfo[]> {
  metrics.forEach(m => m.dataPointsRequested = true);
  const metricsResource = ServiceLocator.get<MetricDataPoints>('MetricDataPoints');
  const ids = metrics.map(m => m.id);
  const result = await metricsResource.getMetricDataPoints(ids);
  if (result.data) {
    Object.entries(result.data).forEach(([id, dataPoints]) => {
      let metric = metrics.find(m => m.id === id);
      if(metric){
        metric.schema = dataPoints.schema;
        metric.dataPoints = parseDataPoints(dataPoints);
        metric.dataRefreshedTimestamp = dataPoints.dataRefreshedTimestamp;
      }
    });
  }
  return metrics;
}

const METRICS_PAGE_SIZE = 20;

export function* handleUpdateMetricsData(action: UpdateMetricsDataAction) {
  try {
    if(!action.metrics.length){
        return;
    }

    for (let i = 0; i < action.metrics.length; i += METRICS_PAGE_SIZE) {
      const metrics: ReadonlyArray<IMetricInfo> = yield call(getMetricsData, action.metrics.slice(i, i + METRICS_PAGE_SIZE));
      yield put(updateMetricsDataSucceeded(metrics));
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* handleResumeMetric(action: ResumeMetricReduxAction) {
  try {
    yield call(resumeSuspendedMetric, action.metric);
    yield call(refreshAfterMetricsActionSucceeded);
  } catch(e) {
    yield call(deleteSuspendedMetric, action.metric);
  }
}

export function resumeSuspendedMetric(metric: IMetricInfo) {
  return ServiceLocator.get<ResumeMetricAction>('ResumeMetricAction')
      .resumeMetric(metric);
}

export function* refreshAfterMetricsActionSucceeded() {
  yield put(requestSpaceContents());
}

export function* deleteSuspendedMetric(metric: IMetricInfo) {
  try {
    const shouldDelete: boolean = yield call(openMetricResumeErrorDialog, metric);
    if (!shouldDelete) {
      return;
    }
    yield put(deleteContentAction(metric as IContentInfo));
  } catch(e) {
    yield put(requestFailed(e, Messages.deleteFailed()));
  }
}

export function confirmDeleteMetric(metric: IMetricInfo) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<DeleteContentsAction>('DeleteContentsAction').confirmDeleteContents([metric as IContentInfo], context);
}

export function openMetricResumeErrorDialog() : IPromise<any> {
  let modalInstance: ModalInstance;

  const props: ResumeMetricErrorDialogProps = {
    submit: () => modalInstance && modalInstance.close(true),
    cancel: () => modalInstance && modalInstance.close(false),
  };

  modalInstance = ReactModalService.open(resumeMetricErrorModalOptions(props));
  return modalInstance.result;
}

export function* rootMetricSpaceSagas(): SagaIterator {
  yield takeEvery(UpdateMetricsData, handleUpdateMetricsData);
  yield takeEvery(ResumeMetric, handleResumeMetric);
}
